import type { ReactNode, FunctionComponent } from 'react';
import cx from 'classnames';
import type { LinkProps } from 'react-router';
import { RouterLink } from '../Link';
import { onEnter } from '../../../utils';

export type ListRowProps = {
  //  If present, the row will be rendered as a <RouterLink>
  to?: LinkProps['to'];
  // If true, open the link in a new tab.
  openInNewTab?: boolean;
  //  Optionally make the entire row clickable. This shouldn't be used
  onClick?: () => void;
  testId?: string;
  //  For lists which have checkmarks, this indicates that the row has been selected.
  isSelected?: boolean;
  //  Tightens up the horizontal padding for certain use-cases.
  hasTightPadding?: boolean;
  // Tightens up the vertical padding for certain use-cases.
  hasTightVerticalPadding?: boolean;
  //  Another padding modifier. Shouldn't be true if hasTightPadding is true, as they conflict. Be sure to add preventDefault
  hasCheckbox?: boolean;
  //  Flex-direction for the row  (@default 'column')
  flexDirection?: 'column' | 'row';
  //  Content that goes in the left section of the row.
  left?: ReactNode;
  //  Content that goes in the right section of the row.
  right?: ReactNode;
  // Sets the row type to an interactive button, defaults to true
  isInteractive?: boolean;
  // If set the row will be highlighted with a background color. defaults to FOSSA_GRAY_005
  isHighlighted?: boolean;
};

/**
 * ListRow is a generic row component for lists.  It handles the general
 * structure and padding of a row.  It should generally be placed inside a
 * `ListRowContainer`.
 * It can handle being just a div, being a link to another page in the app, or
 * being a clickable row.
 */
const ListRow: FunctionComponent<ListRowProps> = ({
  to,
  onClick,
  isSelected,
  hasTightPadding,
  hasTightVerticalPadding,
  hasCheckbox,
  flexDirection = 'column',
  testId,
  children,
  left,
  right,
  openInNewTab,
  isInteractive = true,
  isHighlighted,
}) => {
  const conditionalClassnames = cx({
    'fossa-list__row--has-checkbox': hasCheckbox,
    'fossa-list__row--tight': !hasCheckbox && hasTightPadding,
    'fossa-list__row--tight-vertical': !hasCheckbox && hasTightVerticalPadding,
    'fossa-list__row--selected': isSelected,
    'fossa-list__row--highlight': isHighlighted,
  });

  const centerClassnames = cx('fossa-list__row--center', {
    'fossa-list__row--column': flexDirection === 'column',
    'fossa-list__row--row': flexDirection === 'row',
  });

  const content = (
    <>
      {left && <div className="fossa-list__row--left">{left}</div>}
      <div className={centerClassnames}>{children}</div>
      {right && <div className="fossa-list__row--right">{right}</div>}
    </>
  );

  if (to) {
    return (
      <RouterLink
        to={to}
        openInNewTab={openInNewTab}
        className={cx('fossa-list__row', 'fossa-list__row--link', conditionalClassnames)}
        testId={testId}
        tabIndex={0}
        //  Note that Links really shouldn't also have click handlers, however we'll keep this for legacy reasons.
        onClick={onClick}
      >
        {content}
      </RouterLink>
    );
  }
  if (isInteractive) {
    return (
      <div
        role="button"
        className={cx('fossa-list__row', conditionalClassnames)}
        onClick={onClick}
        onKeyDown={onClick ? onEnter<HTMLDivElement>(onClick) : undefined}
        tabIndex={onClick ? 0 : -1}
        data-test-id={testId}
      >
        {content}
      </div>
    );
  }

  // If it's not a link or interactive, it's just a div.
  return (
    <div className={cx('fossa-list__row', conditionalClassnames)} data-test-id={testId}>
      {content}
    </div>
  );
};

export default ListRow;
